import {
  PhoneMockup,
  MockScreenNeeds,
  MockScreenJobs,
  MockScreenShop,
  MockScreenMarketplace,
  MockScreenWallet,
} from "@/components/site/PhoneMockup";
import { HOME } from "@/constants/testIds";
import { ArrowRight, Check, BadgeCheck, BarChart3, MessageCircle, Sparkles, Images, LayoutDashboard } from "lucide-react";

function ModuleSection({
  id,
  chip,
  title,
  titleAccent,
  desc,
  ctaLabel,
  ctaId,
  ctaHref = "#download",
  reverse = false,
  mockup,
  bg = "white",
  extras = null,
}) {
  return (
    <section
      id={id}
      className={`py-20 md:py-28 ${bg === "alt" ? "bg-[#FFF9F5]" : "bg-white"}`}
    >
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className={`grid lg:grid-cols-2 gap-14 items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#FFF3ED] text-[#FF5A00] px-4 py-1.5 text-xs font-semibold">
              {chip}
            </div>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold text-[#1A1918] leading-tight">
              {title} <span className="brand-gradient-text">{titleAccent}</span>
            </h2>
            <p className="mt-5 text-lg text-[#4A4846] leading-relaxed max-w-xl">{desc}</p>

            {extras}

            {ctaLabel && (
              <a
                href={ctaHref}
                data-testid={ctaId}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FF5A00] hover:bg-[#E65100] text-white px-6 py-3.5 font-semibold shadow-lg shadow-orange-500/25 transition-colors"
              >
                {ctaLabel}
                <ArrowRight className="w-4 h-4" />
              </a>
            )}
          </div>
          <div className="relative flex justify-center">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-72 h-72 rounded-full bg-orange-200/40 blur-3xl" />
            </div>
            <div className="animate-float">
              <PhoneMockup>{mockup}</PhoneMockup>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function LocalNeeds() {
  return (
    <ModuleSection
      id="local-needs"
      chip="Local Tasks"
      title="Need Something Done?"
      titleAccent="Post Your Requirement in Seconds."
      desc="Post your requirement, set your budget, share your location, and connect instantly with nearby people ready to help. From home services and local errands to tutoring and everyday tasks, everything starts here."
      ctaLabel="Post a Local Task"
      ctaId={HOME.ctaPostNeed}
      mockup={<MockScreenNeeds />}
    />
  );
}

export function Jobs() {
  return (
    <ModuleSection
      id="jobs"
      chip="Jobs"
      title="Find Opportunities"
      titleAccent="Near You."
      desc="Discover part-time and full-time job opportunities posted by trusted local businesses and individuals. Filter by distance, category, or schedule to find the right opportunity faster."
      ctaLabel="Explore Jobs"
      ctaId={HOME.ctaExploreJobs}
      reverse
      bg="alt"
      mockup={<MockScreenJobs />}
    />
  );
}

export function Shops() {
  const feats = [
    { icon: BadgeCheck, l: "Verified Business Profile" },
    { icon: BarChart3, l: "Business Analytics" },
    { icon: MessageCircle, l: "Customer Enquiries" },
    { icon: Sparkles, l: "Business Subscription Plans" },
    { icon: Images, l: "Photo Gallery" },
    { icon: LayoutDashboard, l: "Business Dashboard" },
  ];

  return (
    <ModuleSection
      id="shops"
      chip="For Businesses"
      title="Grow Your"
      titleAccent="Business Locally."
      desc="Create your business profile, showcase your products and services, and connect directly with nearby customers through calls, WhatsApp, and in-app chat."
      ctaLabel="Register Your Business"
      ctaId={HOME.ctaRegisterShop}
      mockup={<MockScreenShop />}
      extras={
        <ul className="mt-6 grid grid-cols-2 gap-2.5">
          {feats.map(({ icon: Icon, l }) => (
            <li
              key={l}
              className="flex items-center gap-2 text-sm font-medium text-[#1A1918]"
            >
              <Icon className="w-4 h-4 text-[#FF5A00]" />
              {l}
            </li>
          ))}
        </ul>
      }
    />
  );
}

export function Marketplace() {
  return (
    <ModuleSection
      id="marketplace"
      chip="Marketplace"
      title="Buy, Sell &"
      titleAccent="Rent Locally."
      desc="Create listings, upload photos, and connect directly with buyers, sellers, or renters in your city. No middlemen, no hidden charges—just simple local transactions."
      ctaLabel="Explore Marketplace"
      ctaId={HOME.ctaExploreMarketplace}
      reverse
      bg="alt"
      mockup={<MockScreenMarketplace />}
    />
  );
}

export function WalletSection() {
  return (
    <ModuleSection
      id="wallet"
      chip="Wallet & Credits"
      title="Simple Credits."
      titleAccent="Seamless Experience."
      desc="Manage your credits, redeem coupons, purchase subscriptions, and unlock premium features—all from one secure wallet."
      mockup={<MockScreenWallet />}
      extras={
        <ul className="mt-6 space-y-2">
          {[
            "Credits for posting & unlocking",
            "Redeem coupons & rewards",
            "Manage active subscriptions",
          ].map((l) => (
            <li
              key={l}
              className="flex items-center gap-2 text-sm text-[#4A4846]"
            >
              <Check className="w-4 h-4 text-emerald-600" />
              {l}
            </li>
          ))}
        </ul>
      }
    />
  );
}
